import { Injectable } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { of } from 'rxjs/internal/observable/of';
import { Observable } from 'rxjs/internal/Observable';
import { AccountControllerService } from 'api-auth';
import { map } from 'rxjs/internal/operators/map';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { Router } from '@angular/router';
import { Constants } from '../interfaces/constants';
import { MenuItem } from '../interfaces/common_interfaces';
import { UtilsService } from './utils.service';
import { ClientControllerService, ManagementClientSubscriptionResponse } from 'api-client';
import { SharedService } from './shared.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root',
})
export class PermissionsService {
	private clientSubscriptions: ManagementClientSubscriptionResponse[] = [];

	constructor(
		private authentication: AuthenticationService,
		private apiAuthAccount: AccountControllerService,
		private router: Router,
		private utils: UtilsService,
		private apiClient: ClientControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {}

	public checkUserAccessPermission(route: any) {
		const loginData = this.authentication.loginData.getValue();
		if (!loginData || (loginData && !loginData.accessToken)) {
			this.router.navigate(['account/login']);
			return of(false);
		}

		if (loginData && loginData.role && loginData.scopes) {
			if (loginData.role === 'CLIENT' && this.clientSubscriptions.length === 0) {
				return new Observable<boolean>(observer => {
					this.loadClientSubscriptions().then(() => {
						const hasAccess = this.checkUserAccessOnRoute(route, loginData.scopes || []);
						observer.next(hasAccess);
						observer.complete();
					});
				});
			}
			return of(this.checkUserAccessOnRoute(route, loginData.scopes || []));
		} else {
			return this.apiAuthAccount.getAccountDetails().pipe(
				map(response => {
					this.authentication.setLoginData(response);
					return response;
				}),
				switchMap(response => {
					if (response.role === 'CLIENT' && this.clientSubscriptions.length === 0) {
						return new Observable<boolean>(observer => {
							this.loadClientSubscriptions().then(() => {
								const hasAccess = this.checkUserAccessOnRoute(route, response.scopes || []);
								observer.next(hasAccess);
								observer.complete();
							});
						});
					}
					return of(this.checkUserAccessOnRoute(route, response.scopes || []));
				})
			);
		}
	}

	private checkUserAccessOnRoute(route: any, scopes: string[]) {
		if(route == "/client/profile" || route == "/admin/settings"){
			return true
		}
		const componentItem = this.findComponentByRoute(Constants.COMPONENTS_TREE, route);

		if (componentItem && componentItem.scopes && scopes.indexOf(componentItem.scopes[0]) !== -1) {
			if (this.isClientSubscriptionScope(componentItem.scopes) && !this.hasValidSubscriptionForScope(componentItem.scopes)) {
				const hasAnyValidSubscription = this.hasAnyValidSubscription(scopes);
				this.redirectToFirstValidRoute(scopes, !hasAnyValidSubscription);
				return false;
			}
			return true;
		}
		return false;
	}

	private findComponentByRoute(componentsList: any, route: any) {
		let result:any = null;
		componentsList.some((component: any) => {
      		result = null;
			if (component.items) {
				result = this.findComponentByRoute(component.items, route);
			}
			result = result ? result : component;
			if (`/${result.route}` === (route.indexOf('?') > 0 ? route.substring(0, route.indexOf('?')) : route)) {
				return true;
			}
      		result = null;
			return false;
		});
		return result;
	}

	getPermittedContentByScopes(contentType: 'NAVBAR') {
		let contents: any = [];
		const loginData = this.authentication.loginData.getValue();
		let scopes: string[];
		if (loginData && loginData.scopes) {
			scopes = loginData.scopes;
		} else {
			return contents;
		}

		switch (contentType) {
			case 'NAVBAR': {
        contents = this.getPermittedContentInNavbar(scopes, Constants.COMPONENTS_TREE);
				break;
			}
		}
		return contents;
	}
  private getPermittedContentInNavbar(scopes: string[], list: MenuItem[]) {
    let result: MenuItem[] = [];
    let subItems: MenuItem[] = [];
    list.forEach(component => {
      subItems = [];
      if (scopes.find(scope => component.scopes?.indexOf(scope) !== -1)) {
        if (this.isClientSubscriptionScope(component.scopes) && !this.hasValidSubscriptionForScope(component.scopes)) {
          return;
        }
        if (component.items && component.items.length) {
          subItems = (this.getPermittedContentInNavbar(scopes, component.items));
          if (subItems && subItems.length) {
            component.items = subItems;
          } else {
            this.utils.removeObjectParameterByName(component, 'items');
          }
        }
        result.push(component);
      }
    });
    return result;
  }

  private isClientSubscriptionScope(scopes?: string[]): boolean {
    if (!scopes) return false;
    const clientSubscriptionScopes = ['client:clipping', 'client:transcriptions', 'client:contract_notices', 'client:contract_announcements', 'client:sales', 'client:bankruptcies', 'client:analytics'];
    return scopes.some(scope => clientSubscriptionScopes.includes(scope));
  }

  public hasValidSubscriptionForScope(scopes?: string[]): boolean {
    if (!scopes) return false;

    const loginData = this.authentication.loginData.getValue();
    const userEmail = loginData?.identifier?.toLowerCase();
    if (!userEmail) return false;

    const scopeToSubscriptionMap: { [key: string]: string[] } = {
      'client:clipping': ['CLIPPING'],
	  'client:transcriptions': ['TRANSCRIPTIONS'],
      'client:contract_notices': ['CONTRACT_NOTICES', 'CONTRACT_NOTICES_KOSOVO', 'CONTRACT_NOTICES_ALBANIA'],
      'client:contract_announcements': ['CONTRACT_NOTICES_ANNOUNCEMENTS', 'CONTRACT_NOTICES_ANNOUNCEMENTS_KOSOVO', 'CONTRACT_NOTICES_ANNOUNCEMENTS_ALBANIA'],
      'client:sales': ['SALES'],
      'client:bankruptcies': ['BANKRUPTCIES'],
      'client:analytics': ['ANALYTICS']
    };

    return scopes.some(scope => {
      const subscriptionTypes = scopeToSubscriptionMap[scope];
      if (!subscriptionTypes) return true;

      return subscriptionTypes.some(type => {
        const subscription = this.clientSubscriptions.find(sub => sub.type === type);
        if (!subscription) return false;

        const emailTo = subscription.emailTo?.map(e => e.toLowerCase()) || [];
        const emailCc = subscription.emailCc?.map(e => e.toLowerCase()) || [];
        const emailBcc = subscription.emailBcc?.map(e => e.toLowerCase()) || [];
        const isEmailAuthorized = emailTo.includes(userEmail) || emailCc.includes(userEmail) || emailBcc.includes(userEmail);

        if (!isEmailAuthorized) return false;

        if (!subscription.validTo) return true;
        return new Date(subscription.validTo) >= new Date();
      });
    });
  }

  private hasAnyValidSubscription(scopes: string[]): boolean {
    const clientSubscriptionScopes = ['client:clipping', 'client:transcriptions', 'client:contract_notices', 'client:contract_announcements', 'client:sales', 'client:bankruptcies', 'client:analytics'];

    return scopes.some(scope => {
      if (clientSubscriptionScopes.includes(scope)) {
        return this.hasValidSubscriptionForScope([scope]);
      }
      return false;
    });
  }

  private redirectToFirstValidRoute(scopes: string[], showNoSubscriptionWarning = false) {
    const routePriority = ['clipping', 'transcriptions', 'contract-notices', 'contract-announcements', 'sales', 'bankruptcies', 'analytics'];

    for (const routeName of routePriority) {
      const scopeName = `client:${routeName.replaceAll('-', '_')}`;
      if (scopes.includes(scopeName) && this.hasValidSubscriptionForScope([scopeName])) {
        this.router.navigate([`client/data-preview/${routeName}`]);
        return;
      }
    }

    this.router.navigate(['client/profile']).then(() => {
      if (showNoSubscriptionWarning) {
        this.sharedService.showNotification.next({
          message: this.translate.instant('NOTIFICATIONS.NO_ACTIVE_SUBSCRIPTION'),
          type: 'WARNING',
        });
      }
    });
  }

  public loadClientSubscriptions(): Promise<void> {
    const loginData = this.authentication.loginData.getValue();
    if (loginData?.role === 'CLIENT') {
      return new Promise((resolve) => {
        this.apiClient.getClientDetails().subscribe({
          next: (clientDetails) => {
            this.clientSubscriptions = clientDetails.subscriptions || [];
            resolve();
          },
          error: () => {
            this.clientSubscriptions = [];
            resolve();
          }
        });
      });
    } else {
      return Promise.resolve();
    }
  }
}
